import { AnimatePresence, motion } from "framer-motion";
import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import {
  CharacterLimitIcon,
  DelIconSvg,
  DownArrIconSvg,
  IErrIconSvg,
  LinkRejectedIcon,
  ReSyncingFailedIcon,
  ReSyncingIcon,
  RetrainBtnIcon,
  TrainingCompletedIcon,
  TrainingProgressIcon,
  ViewDetailsIcon,
  PdfSmlIcon,
  ExcelSmlIcon,
  CsvSmlIcon,
  MdSmlIcon,
  TxtSmlIcon,
  DocSmlIcon,
} from "../AiBotAssets";
import useFormatNumber from "@/hooks/UseFormatNumber";
import {
  NoFilesIcon,
  ProgressBar,
  DelIcon,
  WithoutBgDeleteIcon,
} from "@/assets/AiBotAssets";
import Lottie from "react-lottie";
import animationData from "../../../.././assets/LottiFiles/UploadSource.json";
import { createPortal } from "react-dom";
import { statuses } from "../AIBotTab_1";
import { useDispatch, useSelector } from "react-redux";
import { State } from "@/redux/Types";
import { axiosPost } from "@/services/axiosService";
import { BotContext } from "../../store";
import { userSlice } from "@/redux/User";
import { sendEventToOutgrow } from "../../outgrow-event.service";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Pagination from "../../Pagination";

const onboardingStatusIcons: Record<string, React.ReactNode> = {
  "Training Complete": <TrainingCompletedIcon />,
  "In Progress": <TrainingProgressIcon />,
  "Link Rejected": <LinkRejectedIcon />,
  "Limit Reached": <LinkRejectedIcon />,
  "File Rejected": <LinkRejectedIcon />,
  "Re-syncing": <ReSyncingIcon />,
  "Re-sync Complete": <TrainingCompletedIcon />,
  "Re-sync Failed": <ReSyncingFailedIcon />,
};
interface dropdowns {
  linkStatus: boolean;
  fileStatus: boolean;
  fileAction: boolean;
  syncTime: boolean;
  linkCharacters: boolean;
  linkLastSynced: boolean;
  linkAutoSync: boolean;
  trainedappsAction: boolean;
  untrainedappsAction: boolean;
  appsAutoSync: boolean;
}
const dropdownState = {
  linkStatus: false,
  fileStatus: false,
  fileAction: false,
  syncTime: false,
  linkCharacters: false,
  linkLastSynced: false,
  linkAutoSync: false,
  trainedappsAction: false,
  untrainedappsAction: false,
  appsAutoSync: false,
};

type UploadFilesProps = {
  setSelectedIds: React.Dispatch<any>;
  setInProcess: React.Dispatch<any>;
  selectedIds: any[];
  onFileRemove: (index: number) => void;
  isPostTrainingDelay: boolean;
  showProgress: boolean;
  handleFileRemove: (removeFn: any, data: any) => void | Promise<void>;
  uploadProgress: number;
  uploadStatus?: string;
  trainedFiles: any[];
  isOld: boolean;
  characterLimit: number;
  characterUsed: number;
  files: any[];
  botData: any;
  plan: any;
  onFileUpload: (event: any) => void;
  parentForceReload?: (...args: any[]) => void;
  refetchCompany?: (...args: any[]) => void;
  isRetraining?: boolean;
  retryRejectedLinks?: (files: any[], type: string) => void;
  setSelectedStatus?: (value: any) => void;
  inProcess: boolean;
  handleDropdowns?: (...args: any[]) => void;
  handleViewDetails?: (...args: any[]) => void;
  deleteTrainDocument?: (data: any) => void | Promise<void>;
  onTrainAgent?: () => void | Promise<void>;
  onDeleteSelectedTrainedFiles?: (selectedFiles: any[]) => void | Promise<void>;
  fromOnboarding?: boolean;
  isReadOnly?: boolean;
};

const getFileIcon = (filename: string) => {
  const ext = filename?.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return <PdfSmlIcon />;
    case "xls":
    case "xlsx":
      return <ExcelSmlIcon />;
    case "csv":
      return <CsvSmlIcon />;
    case "md":
      return <MdSmlIcon />;
    case "txt":
      return <TxtSmlIcon />;
    case "doc":
    case "docx":
      return <DocSmlIcon />;
    default:
      return null;
  }
};

const UploadFiles = ({
  setSelectedIds,
  setInProcess,
  selectedIds,
  onFileRemove,
  isPostTrainingDelay,
  showProgress,
  handleFileRemove,
  uploadProgress,
  uploadStatus,
  trainedFiles,
  isOld,
  characterLimit,
  characterUsed,
  files,
  botData,
  plan,
  onFileUpload,
  parentForceReload,
  refetchCompany,
  isRetraining,
  retryRejectedLinks,
  setSelectedStatus,
  inProcess,
  handleDropdowns,
  handleViewDetails,
  deleteTrainDocument,
  onTrainAgent = undefined,
  onDeleteSelectedTrainedFiles = undefined,
  fromOnboarding,
  isReadOnly = false,
}: UploadFilesProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [buyAddOn, setBuyAddOn] = useState(false);
  const fromOutgrow = useSelector((state: State) => state.user.fromOutgrow);
  const [store, storeDispatch] = useContext(BotContext);
  const [leaveModal, setLeaveModal] = useState(false);
  const [isStopped, setIsStopped] = useState(true);
  const [fileSearchQuery, setFileSearchQuery] = useState("");
  const [fileCurrentPage, setFileCurrentPage] = useState(1);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 0,
  });
  const [dropdowns, setDropdowns] = useState<any>({});
  const [drpToggle, setDrpToggle] = useState<dropdowns>(dropdownState);
  const dropdownRef = useRef<any>({});
  const [selectedFileStatus, setSelectedFileStatus] = useState<{
    icon: React.ReactNode;
    text: string;
  }>({
    icon: null,
    text: "",
  });
  const file = botData.aiSource?.documents || [];

  const itemsPerPage = 10;
  const isFileTrainingRateLimited = useMemo(
    () => (file ?? []).some((data) => data.status === "Limit Reached"),
    [file],
  );

  const size = plan?.ai_currentLimits?.fileSize
    ? (plan.ai_currentLimits.fileSize / (1024 * 1024)).toFixed(2)
    : 0;

  const defaultOptions = {
    loop: false,
    autoplay: false,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };
  const getFileSelectionKey = useCallback((item: any) => {
    return [
      item?.id ?? "",
      item?.title ?? item?.name ?? "",
      item?.size ?? "",
    ].join("::");
  }, []);

  const filteredTrainedFiles = useMemo(() => {
    const statusFilteredFiles =
      selectedFileStatus.text === "All Statuses" ||
      selectedFileStatus.text === ""
        ? trainedFiles
        : trainedFiles.filter(
            (trainedFile) => trainedFile.status === selectedFileStatus.text,
          );

    if (!fileSearchQuery) {
      return statusFilteredFiles;
    }

    return statusFilteredFiles.filter((trainedFile) => {
      const titleOrName = trainedFile.title || trainedFile.name || "";
      return titleOrName.toLowerCase().includes(fileSearchQuery.toLowerCase());
    });
  }, [fileSearchQuery, selectedFileStatus.text, trainedFiles]);

  const totalFilteredPages = Math.max(
    1,
    Math.ceil(filteredTrainedFiles.length / itemsPerPage),
  );

  useEffect(() => {
    if (!fromOnboarding && fileCurrentPage > totalFilteredPages) {
      setFileCurrentPage(totalFilteredPages);
    }
  }, [fileCurrentPage, fromOnboarding, totalFilteredPages]);

  const displayedTrainedFiles = useMemo(() => {
    if (fromOnboarding) {
      return filteredTrainedFiles;
    }

    const startIndex = (fileCurrentPage - 1) * itemsPerPage;
    return filteredTrainedFiles.slice(startIndex, startIndex + itemsPerPage);
  }, [fileCurrentPage, filteredTrainedFiles, fromOnboarding]);

  const selectableDisplayedFiles = useMemo(
    () => displayedTrainedFiles,
    [displayedTrainedFiles],
  );

  const selectedAll = useMemo(() => {
    if (!selectableDisplayedFiles.length) return false;

    const selectedKeys = new Set(selectedIds.map(getFileSelectionKey));
    return selectableDisplayedFiles.every((trainedFile) =>
      selectedKeys.has(getFileSelectionKey(trainedFile)),
    );
  }, [getFileSelectionKey, selectableDisplayedFiles, selectedIds]);

  useEffect(() => {
    const availableKeys = new Set(trainedFiles.map(getFileSelectionKey));
    setSelectedIds((currentSelection: any[]) =>
      currentSelection.filter((item) =>
        availableKeys.has(getFileSelectionKey(item)),
      ),
    );
  }, [getFileSelectionKey, setSelectedIds, trainedFiles]);
  const rejectedFiles = useMemo(
    () => [
      ...(Array.isArray(file)
        ? file.filter((file) => file.status === "File Rejected")
        : []),
    ],
    [file],
  );
  const renderFileType = useCallback((type) => {
    return type.split(".").at(-1);
  }, []);
  const formatUploadedFileSize = useCallback((fileSize?: number) => {
    if (!fileSize) return "--";
    return fileSize / (1024 * 1024) >= 0.01
      ? `${(fileSize / (1024 * 1024)).toFixed(2)} MB`
      : `${(fileSize / 1024).toFixed(2)} KB`;
  }, []);

  function toggleDropdowns(type: string, show: boolean = false) {
    if (show) {
      setDrpToggle({ ...dropdownState, [type]: show });
    } else {
      setDrpToggle(dropdownState);
    }
  }

  const handleFileActionDropdowns = (rowId, type, value) => {
    const triggerKey = `${type}-${rowId}`;
    const triggerElement = dropdownRef.current[triggerKey];

    if (triggerElement && value) {
      const rect = triggerElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      requestAnimationFrame(() => {
        const dropdownId = `#dropdown-${type}-${rowId}`;
        const observer = new MutationObserver(() => {
          const dropdownElement = document.querySelector(dropdownId);
          if (!dropdownElement) return;

          const dropdownRect = dropdownElement.getBoundingClientRect();
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          const shouldOpenUpwards =
            dropdownRect.height > spaceBelow && spaceAbove > spaceBelow;

          setDropdownPosition({
            top: shouldOpenUpwards
              ? rect.top + window.scrollY - dropdownRect.height
              : rect.bottom + window.scrollY,
            left: rect.right + window.scrollX - 150,
            width: rect.width,
            maxHeight: shouldOpenUpwards
              ? rect.top + window.scrollY
              : rect.bottom + window.scrollY,
          });

          observer.disconnect();
        });

        observer.observe(document.body, { childList: true, subtree: true });

        setDropdowns((prevState) => ({
          ...prevState,
          [rowId]: {
            ...(prevState[rowId] || {}),
            [type]: value,
          },
        }));
      });
    } else {
      setDropdowns((prevState) => ({
        ...prevState,
        [rowId]: {
          ...(prevState[rowId] || {}),
          [type]: value,
        },
      }));
    }
  };

  const handleFilePageChange = (newPage) => {
    setFileCurrentPage(newPage);
  };

  const handleSearchFiles = (event) => {
    setFileSearchQuery(event.target.value);
    setFileCurrentPage(1);
  };
  const handleFileStatusChange = (status) => {
    setSelectedFileStatus({
      text: status.text,
      icon: status.iconComponent,
    });
    setFileCurrentPage(1);
  };
  function selectAll() {
    if (isPostTrainingDelay) return;
    if (selectedAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableDisplayedFiles);
    }
  }

  function toggleSelection(item: any) {
    if (isPostTrainingDelay) return;
    // if (item?.status === "In Progress") return;
    // console.log(selectedIds);
    setSelectedIds((prevSelected: any) => {
      const itemKey = getFileSelectionKey(item);
      const exists = prevSelected.some(
        (selectedItem) => getFileSelectionKey(selectedItem) === itemKey,
      );

      return exists
        ? prevSelected.filter(
            (selectedItem) => getFileSelectionKey(selectedItem) !== itemKey,
          )
        : [...prevSelected, item];
    });
  }
  async function deleteMultipleTrainDocument() {
    try {
      if (!selectedIds.length) throw new Error("No source found to delete");
      const source = selectedIds;
      let size = 0;
      // for (const data of displayedTrainedFiles) {
      //   if (selectedIds.includes(data.title)) {
      //     size += data.size;
      //   }
      // }

      setInProcess(true);
      const res = await axiosPost("/ai/delete-content", {
        sources: source,
        size: size ?? 0,
        botCustomId: botData.botCustomId,
        botCreatedAt: botData.createdAt,
        sourceType: "documents",
      });

      // storeDispatch({
      //   type: "set-characterCount",
      //   characterCount: res.data.characterUsed,
      // });
      storeDispatch({
        type: "set-botData",
        botData: {
          ...store.botData,
          aiBotConfig: {
            ...store.botData.aiBotConfig,
            characterUsed: res.data.characterUsed,
          },
        },
      });
      ``;
      parentForceReload?.(true);

      // Refetch company data to get updated character limits
      if (refetchCompany) {
        refetchCompany();
      }

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message:
            "Selected data has been successfully removed from the training data.",
        }),
      );

      setSelectedIds((selectedIds) =>
        selectedIds.filter((id) => !source?.includes(id)),
      );
    } catch (err: any) {
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: err.message,
        }),
      );
    } finally {
      setInProcess(false);
    }
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={`uploadFile fx-d-col h-100 upload-source-wrapper relative flex ${fromOnboarding ? "onboarding-file-upload !pb-[0px]" : ""} ${files?.length || botData.dataSources?.documents?.length ? "" : "pb-[20px]"} `}
    >
      {!fromOnboarding && (
        <h3 className="!mb-[10px] flex items-center gap-[10px] text-left">
          Upload Data
          {isOld && (
            <div className="character-count-wrapper flex items-center gap-[10px]">
              <span className="!ms-[0px] h-[24px] w-[1px] bg-[#666] opacity-[0.3]"></span>
              <p
                className={`character-count pointer relative z-[5] flex items-center gap-[4px] rounded-[5px] px-[8px] py-[6px] text-[13px] font-medium capitalize leading-normal ${useFormatNumber(characterUsed) === useFormatNumber(characterLimit) ? "bg-[#FFE8E9] text-[#fb5f66]" : "bg-[#FEF2CC] text-[#9A6815]"} `}
              >
                <CharacterLimitIcon
                  color={
                    useFormatNumber(characterUsed) ===
                    useFormatNumber(characterLimit)
                      ? "#fb5f66"
                      : "#9A6815"
                  }
                />
                {characterLimit === 0 ? (
                  <div className="yellow-loader"></div>
                ) : (
                  `${useFormatNumber(characterUsed)} / ${useFormatNumber(characterLimit)}`
                )}
                <div className="none absolute left-[0] top-[35px] z-[1] w-[280px] cursor-default flex-col items-center gap-[10px] rounded-[8px] bg-[#222] p-[10px]">
                  {useFormatNumber(characterUsed) ===
                  useFormatNumber(characterLimit) ? (
                    <p className="text-center text-[12px] font-normal normal-case not-italic leading-[17px] text-[#fb5f66]">
                      Your current plan doesn't support more than
                      <b className="font-medium">
                        {" "}
                        {useFormatNumber(characterLimit)} Characters.{" "}
                      </b>
                      {plan.name !== "Professional"
                        ? "Upgrade to the Professional plan to unlock this feature."
                        : "If you want more, you have to buy the add-on."}
                    </p>
                  ) : (
                    <p className="text-center text-[12px] font-normal normal-case not-italic leading-[17px] text-white/80">
                      Agents can use a maximum of <br />
                      <b className="font-medium">
                        {useFormatNumber(characterLimit)} Characters
                      </b>
                      , including link content. Anything over that limit won't
                      be used.
                    </p>
                  )}
                  {plan.name !== "Professional" ? (
                    <button
                      className="pointer h-[30px] w-full rounded-[8px] bg-[#fb5f66] text-[12px] font-normal leading-normal text-white"
                      onClick={() => {
                        setBuyAddOn(false);
                        setLeaveModal(true);
                      }}
                    >
                      Upgrade Plan
                    </button>
                  ) : (
                    <button
                      className="pointer h-[30px] w-full rounded-[8px] bg-[#fb5f66] text-[12px] font-normal leading-normal text-white"
                      onClick={() => {
                        setBuyAddOn(true);
                        setLeaveModal(true);
                      }}
                    >
                      Buy add-on
                    </button>
                  )}
                </div>
              </p>
            </div>
          )}
        </h3>
      )}
      <div className="file-uploads-inner fx w-100 fx-d-col gap-20 overflow-auto">
        <input
          type="file"
          name="file-input"
          id="file-input"
          className="file-input__input"
          accept=".pdf,.txt,.docx,.xlsx,.csv,.md"
          multiple={true}
          value=""
          onChange={(event) => {
            onFileUpload(event);
          }}
          disabled={isReadOnly || showProgress}
        />
        <label
          htmlFor="file-input"
          className={`upload-box-wrapper ${isReadOnly ? "pointer-events-none opacity-60" : ""}`}
        >
          <div
            className="upload-box flex-cnt fx-d-col w-auto"
            onMouseEnter={() => setIsStopped(false)}
            onMouseLeave={() => setIsStopped(true)}
          >
            {!showProgress && (
              <div className="upload-files flex-cnt fx-d-col">
                <div className="upload-icon-frame">
                  <Lottie
                    options={defaultOptions}
                    height={150}
                    width={150}
                    isStopped={isStopped}
                  />
                </div>
                <motion.span
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.85 }}
                  className="browse-btn fw-500 mt-10"
                >
                  Upload File
                </motion.span>
                <p className="file-size">{`Max File Size: ${size} MB`}</p>
                <div className="uploadDetails fx">
                  <p>Supported formats: PDF, DOCX , TXT , MD , XLSX, CSV</p>
                </div>
              </div>
            )}
            {showProgress && (
              <div className="progress-bar flex-cnt fx-d-col">
                <ProgressBar progress={uploadProgress} />
                {uploadStatus && (
                  <p className="mt-[8px] text-[12px] leading-[140%] text-[#6D6D6D]">
                    {uploadStatus}
                  </p>
                )}
              </div>
            )}
          </div>
        </label>
        {files?.length > 0 && (
          <div className="fx-d-col flex h-max">
            <h4 className="tabs_inner_head mb-10 font-medium text-text-clr">
              {fromOnboarding ? "Uploaded Files" : "Uploaded Data"}
            </h4>
            {files?.length || botData.dataSources?.documents?.length ? (
              <div
                className={`uploadedFilegrid h-max ${fromOnboarding ? "uploadedFilegrid-onboarding" : ""}`}
              >
                <div
                  className={`${fromOnboarding ? "new-scrollbar overflow-x-auto overflow-y-hidden" : ""}`}
                >
                  <table className="mt-0 overflow-hidden">
                    <thead
                      className={`sticky top-0 h-[54px] bg-[#F2F2F2] ${files.length > 5 ? "pr-[9.5px]" : ""} ${files.length >= 5 && fromOnboarding ? "!pr-[5px]" : ""}`}
                    >
                      <th>File Name</th>
                      <th>Size</th>
                      <th>Character</th>
                      <th>Action</th>
                    </thead>
                    <tbody
                      className={`flex max-h-[278px] flex-col overflow-auto bg-white ${fromOnboarding ? "new-scrollbar" : "grey-scrollbar"} ${files.length < 5 ? "overflow-hidden" : ""}`}
                    >
                      <AnimatePresence>
                        {files?.map((file, index) => (
                          <motion.tr
                            key={file.id || index}
                            className={`${files.length > 10 ? "no-right-border" : ""}`}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{
                              opacity: 0,
                              height: 0,
                              transition: { duration: 0.5 },
                            }}
                            layout
                          >
                            <td className="w-100 overflow-hidden text-ellipsis whitespace-nowrap">
                              <span
                                className="fx trainedLinksUrl gap-8 overflow-hidden whitespace-nowrap"
                                onMouseEnter={(e) => {
                                  const container = e.currentTarget;
                                  const inner = container.querySelector(
                                    ".scrolling-text",
                                  ) as HTMLElement | null;
                                  if (inner) {
                                    const scrollDistance =
                                      inner.scrollWidth - container.offsetWidth;
                                    if (scrollDistance > 0) {
                                      const speed = 100;
                                      const duration = scrollDistance / speed;
                                      inner.style.transition = `transform ${duration}s linear`;
                                      inner.style.transform = `translateX(-${scrollDistance}px)`;
                                    }
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.currentTarget;
                                  const inner = container.querySelector(
                                    ".scrolling-text",
                                  ) as HTMLElement | null;
                                  if (inner) {
                                    inner.style.transition =
                                      "transform 0.3s ease-out";
                                    inner.style.transform = "translateX(0)";
                                  }
                                }}
                              >
                                <div
                                  className={`scrolling-text flex items-center ${fromOnboarding ? "gap-[5px]" : "gap-2"} whitespace-nowrap`}
                                >
                                  {fromOnboarding &&
                                    getFileIcon(file?.title || file?.name)}
                                  <span title={file.title || file.name}>
                                    {" "}
                                    {file.title || file.name}
                                  </span>
                                </div>
                              </span>
                            </td>

                            <td className="uploadDetails fx mt-0">
                              <p className="!text-[14px] !text-[#444]">
                                {formatUploadedFileSize(file?.size)}
                              </p>
                            </td>

                            <td className="uploadDetails fx mt-0">
                              <p className="!text-[14px] !text-[#444]">
                                {file ? "Post-training" : file?.text?.length}
                              </p>
                            </td>
                            <td className="flex items-center justify-center">
                              <button
                                type="button"
                                onClick={() => onFileRemove(index)}
                              >
                                {fromOnboarding ? (
                                  <WithoutBgDeleteIcon />
                                ) : (
                                  <svg
                                    className="cursor-pointer"
                                    width="32"
                                    height="32"
                                    viewBox="0 0 32 32"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <rect
                                      x="0.302734"
                                      width="30.7265"
                                      height="32"
                                      rx="8"
                                      fill="#FFE0E1"
                                    />
                                    <path
                                      d="M21.393 11.9088C21.176 11.9088 20.9679 11.995 20.8145 12.1485C20.661 12.3019 20.5748 12.51 20.5748 12.727V21.8828C20.5514 22.2965 20.3654 22.6842 20.0574 22.9614C19.7493 23.2386 19.3443 23.3829 18.9304 23.3628H12.4016C11.9877 23.3829 11.5826 23.2386 11.2746 22.9614C10.9666 22.6842 10.7806 22.2965 10.7572 21.8828V12.727C10.7572 12.51 10.671 12.3019 10.5175 12.1485C10.3641 11.995 10.156 11.9088 9.93901 11.9088C9.72203 11.9088 9.51393 11.995 9.3605 12.1485C9.20707 12.3019 9.12087 12.51 9.12087 12.727V21.8828C9.14423 22.7306 9.5026 23.5345 10.1175 24.1186C10.7324 24.7027 11.5538 25.0193 12.4016 24.9991H18.9304C19.7782 25.0193 20.5996 24.7027 21.2145 24.1186C21.8294 23.5345 22.1878 22.7306 22.2111 21.8828V12.727C22.2111 12.51 22.1249 12.3019 21.9715 12.1485C21.8181 11.995 21.61 11.9088 21.393 11.9088ZM22.2111 9.45442H18.9386V7.81814C18.9386 7.60116 18.8524 7.39306 18.6989 7.23963C18.5455 7.0862 18.3374 7 18.1204 7H13.2116C12.9946 7 12.7865 7.0862 12.6331 7.23963C12.4796 7.39306 12.3934 7.60116 12.3934 7.81814V9.45442H9.12087C8.90389 9.45442 8.69579 9.54062 8.54236 9.69405C8.38893 9.84748 8.30273 10.0556 8.30273 10.2726C8.30273 10.4895 8.38893 10.6976 8.54236 10.8511C8.69579 11.0045 8.90389 11.0907 9.12087 11.0907H22.2111C22.4281 11.0907 22.6362 11.0045 22.7896 10.8511C22.9431 10.6976 23.0293 10.4895 23.0293 10.2726C23.0293 10.0556 22.9431 9.84748 22.7896 9.69405C22.6362 9.54062 22.4281 9.45442 22.2111 9.45442ZM14.0297 9.45442V8.63628H17.3023V9.45442H14.0297Z"
                                      fill="#FB5F66"
                                    />
                                    <path
                                      opacity="0.5"
                                      d="M14.8472 20.09V14.3631C14.8472 14.1461 14.761 13.938 14.6076 13.7845C14.4542 13.6311 14.2461 13.5449 14.0291 13.5449C13.8121 13.5449 13.604 13.6311 13.4506 13.7845C13.2971 13.938 13.2109 14.1461 13.2109 14.3631V20.09C13.2109 20.307 13.2971 20.5151 13.4506 20.6686C13.604 20.822 13.8121 20.9082 14.0291 20.9082C14.2461 20.9082 14.4542 20.822 14.6076 20.6686C14.761 20.5151 14.8472 20.307 14.8472 20.09ZM18.1198 20.09V14.3631C18.1198 14.1461 18.0336 13.938 17.8802 13.7845C17.7267 13.6311 17.5186 13.5449 17.3016 13.5449C17.0847 13.5449 16.8766 13.6311 16.7231 13.7845C16.5697 13.938 16.4835 14.1461 16.4835 14.3631V20.09C16.4835 20.307 16.5697 20.5151 16.7231 20.6686C16.8766 20.822 17.0847 20.9082 17.3016 20.9082C17.5186 20.9082 17.7267 20.822 17.8802 20.6686C18.0336 20.5151 18.1198 20.307 18.1198 20.09Z"
                                      fill="#FB5F66"
                                    />
                                  </svg>
                                )}
                              </button>
                            </td>
                          </motion.tr>
                        ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
                {onTrainAgent && (
                  <div
                    className={`${fromOnboarding ? "uploadedFilegrid-onboarding-footer" : "mt-[12px] flex justify-end"}`}
                  >
                    <button
                      type="button"
                      className={`save-btn ${fromOnboarding ? "uploadedFilegrid-onboarding-train-btn" : ""} ${showProgress || !files?.length ? "disable" : ""}`}
                      onClick={() => {
                        if (showProgress || !files?.length) return;
                        onTrainAgent();
                      }}
                    >
                      Train Agent
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="uploadedFile h-100">
                <div className="url-crawl ai-info-wrapper flex-cnt fx-d-col no-mt h-100">
                  <NoFilesIcon />
                  <h4>No file to show</h4>
                </div>
              </div>
            )}
          </div>
        )}
        <div
          className={`flex h-max flex-col pt-20 ${fromOnboarding ? (trainedFiles?.length || botData.aiSource?.documents?.length ? "" : "none") : "border-[rgba(102, 102, 102, 0.20)] mt-20 border-t"}`}
        >
          <div className="flex items-center justify-between gap-[12px]">
            <h2 className="text-[16px] font-medium text-text-clr">
              Trained Files{" "}
              {fromOnboarding && (
                <span
                  style={{
                    color: "#666",
                    fontSize: "14px",
                    fontWeight: "normal",
                  }}
                >
                  ({displayedTrainedFiles?.length ?? 0})
                </span>
              )}
            </h2>
            {fromOnboarding && selectedIds.length >= 1 && (
              <button
                type="button"
                className="flex h-[30px] items-center justify-center gap-[5px] rounded-[8px] bg-[#FFF7F7] px-[8px] text-[#fb5f66] transition-colors"
                onClick={() => {
                  if (onDeleteSelectedTrainedFiles) {
                    onDeleteSelectedTrainedFiles(selectedIds);
                    return;
                  }
                  handleFileRemove(deleteMultipleTrainDocument, []);
                }}
              >
                <WithoutBgDeleteIcon />
                <span className="mt-[2px]">Delete Files</span>
              </button>
            )}
          </div>
          <div
            className={`trained_links_wrapper files_table_wrapper p-relative new-scrollbar mt-10 ${fromOnboarding ? "trained_links_wrapper-onboarding !overflow-hidden" : ""} ${displayedTrainedFiles?.length === 0 ? "!mb-0" : ""}`}
          >
            {trainedFiles?.length || botData.aiSource?.documents?.length ? (
              <>
                {!fromOnboarding && (
                  <div className="table_actions sticky left-0 top-0 z-[2] flex items-center justify-between gap-10 bg-[#f9f9f9] px-[20px] py-3">
                    <div className="flex items-center gap-10">
                      <div className="search_div border-[rgba(0, 0, 0, 0.10)] h[44px] flex w-[300px] items-center gap-10 rounded-[10px] border border-solid bg-white px-[16px] py-[13px]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={15}
                          height={14}
                          viewBox="0 0 15 14"
                          fill="none"
                        >
                          <g clipPath="url(#clip0_190_16528)">
                            <path
                              d="M5.60625 0C6.98538 0 8.30802 0.547855 9.28321 1.52304C10.2584 2.49823 10.8063 3.82087 10.8063 5.2C10.8063 6.488 10.3342 7.672 9.55825 8.584L9.77425 8.8H10.4062L14.4062 12.8L13.2063 14L9.20625 10V9.368L8.99025 9.152C8.04669 9.95742 6.84682 10.3999 5.60625 10.4C4.22712 10.4 2.90448 9.85215 1.92929 8.87696C0.954105 7.90177 0.40625 6.57913 0.40625 5.2C0.40625 3.82087 0.954105 2.49823 1.92929 1.52304C2.90448 0.547855 4.22712 0 5.60625 0ZM5.60625 1.6C3.60625 1.6 2.00625 3.2 2.00625 5.2C2.00625 7.2 3.60625 8.8 5.60625 8.8C7.60625 8.8 9.20625 7.2 9.20625 5.2C9.20625 3.2 7.60625 1.6 5.60625 1.6Z"
                              fill="#444444"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_190_16528">
                              <rect
                                width={14}
                                height={14}
                                fill="white"
                                transform="translate(0.40625)"
                              />
                            </clipPath>
                          </defs>
                        </svg>
                        <input
                          type="text"
                          placeholder="Search File"
                          className="h-[unset] border-none bg-transparent p-0 outline-none"
                          value={fileSearchQuery}
                          onChange={handleSearchFiles}
                        />
                      </div>

                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const viewportHeight = window.innerHeight;
                          const spaceBelow = viewportHeight - rect.bottom;
                          const maxHeight = Math.min(spaceBelow - 20, 300); // 20px padding from viewport edge
                          setDropdownPosition({
                            top: rect.bottom + window.scrollY,
                            left: rect.left + window.scrollX,
                            width: rect.width,
                            maxHeight: maxHeight > 50 ? maxHeight : 150,
                          });
                          toggleDropdowns("fileStatus", !drpToggle.fileStatus);
                        }}
                        onMouseLeave={() =>
                          toggleDropdowns("fileStatus", false)
                        }
                        className="status_dropdown statuses_drp cp border-[rgba(0, 0, 0, 0.10)] relative flex !h-[44px] w-max items-center justify-between gap-[10px] rounded-[10px] border border-solid bg-white px-[16px] py-[12px]"
                      >
                        {selectedFileStatus.text ? (
                          <span className="flex items-center gap-[6px]">
                            {selectedFileStatus.icon} {selectedFileStatus.text}
                          </span>
                        ) : (
                          "All Statuses"
                        )}
                        <span
                          className={`transition-transform duration-300 ${drpToggle.fileStatus ? "rotate-180" : "rotate-0"}`}
                        >
                          <DownArrIconSvg />
                        </span>

                        {drpToggle.fileStatus &&
                          createPortal(
                            <ul
                              className="status_dropdown_list trained_data-statuses grey-scrollbar absolute z-[999] overflow-auto rounded-[8px]"
                              ref={dropdownRef}
                              style={{
                                top: `${dropdownPosition.top}px`,
                                left: `${dropdownPosition.left}px`,
                                maxHeight: `${dropdownPosition.maxHeight}px`,
                                ...(fromOutgrow && {
                                  fontFamily: '"Ubuntu", sans-serif',
                                }),
                              }}
                              onMouseLeave={() =>
                                toggleDropdowns("fileStatus", false)
                              }
                            >
                              {statuses.map((item, index) => {
                                return (
                                  <li
                                    key={index}
                                    className="flex cursor-pointer items-center gap-[6px]"
                                    onClick={() => {
                                      handleFileStatusChange(item);
                                      toggleDropdowns("fileStatus", false);
                                    }}
                                  >
                                    {item.iconComponent}
                                    {item.text}
                                  </li>
                                );
                              })}
                            </ul>,
                            document.getElementById(
                              "dropdown-portal",
                            ) as HTMLElement,
                          )}
                      </div>
                    </div>
                    <div className="flex items-center gap-10">
                      <span
                        className={`deleteFetchedUrlBtn fx enabled al-cnt gap-8 ${!selectedIds.length ? "pointer-events-none opacity-50" : ""}`}
                        onClick={() => {
                          if (onDeleteSelectedTrainedFiles) {
                            onDeleteSelectedTrainedFiles(selectedIds);
                            return;
                          }
                          handleFileRemove(deleteMultipleTrainDocument, []);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="17"
                          viewBox="0 0 16 17"
                          fill="none"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M15.4382 3.5C15.4382 2.94772 14.9905 2.5 14.4382 2.5H11.9208C11.4996 1.30491 10.372 0.504062 9.10483 0.5H7.10483C5.83767 0.504062 4.71008 1.30491 4.28883 2.5H1.77148C1.2192 2.5 0.771484 2.94772 0.771484 3.5C0.771484 4.05228 1.2192 4.5 1.77148 4.5H2.10483V12.8333C2.10483 14.8584 3.74645 16.5 5.77148 16.5H10.4381C12.4632 16.5 14.1048 14.8584 14.1048 12.8333V4.5H14.4382C14.9905 4.5 15.4382 4.05228 15.4382 3.5ZM12.1048 12.8333C12.1048 13.7538 11.3586 14.5 10.4382 14.5H5.77148C4.85102 14.5 4.10483 13.7538 4.10483 12.8333V4.5H12.1048V12.8333ZM6.43774 12.499C6.99003 12.499 7.43774 12.0513 7.43774 11.499V7.49902C7.43774 6.94674 6.99003 6.49902 6.43774 6.49902C5.88546 6.49902 5.43774 6.94674 5.43774 7.49902V11.499C5.43774 12.0513 5.88546 12.499 6.43774 12.499ZM10.7722 11.499C10.7722 12.0513 10.3245 12.499 9.77222 12.499C9.21994 12.499 8.77222 12.0513 8.77222 11.499V7.49902C8.77222 6.94674 9.21994 6.49902 9.77222 6.49902C10.3245 6.49902 10.7722 6.94674 10.7722 7.49902V11.499Z"
                            fill="#666666"
                          />
                        </svg>
                        Delete Files
                      </span>
                    </div>
                  </div>
                )}
                {isFileTrainingRateLimited ? (
                  <div className="retrain_err sticky left-0 top-0 flex items-center justify-between">
                    <div className="flex items-center gap-[8px]">
                      <IErrIconSvg />
                      <p className="mt-0">
                        {isOld
                          ? "You have reached your training characters limit."
                          : "You don’t have enough credits to train some files. "}
                        Please upgrade your plan.
                      </p>
                    </div>
                    <div className="flex items-center gap-[8px]">
                      <button
                        className="flex items-center gap-[8px]"
                        onClick={() => {
                          fromOutgrow
                            ? sendEventToOutgrow({
                                type: "navigate",
                                payload: {
                                  route: "/settings/membership",
                                },
                              })
                            : navigate("/settings");
                          dispatch(userSlice.actions.setSettings(2));
                        }}
                      >
                        <RetrainBtnIcon /> Upgrade Plan
                      </button>
                    </div>
                  </div>
                ) : (
                  rejectedFiles &&
                  rejectedFiles.length > 0 && (
                    <div
                      className={`retrain_err sticky left-0 top-0 flex items-center justify-between ${fromOnboarding ? "rounded-t-[16px]" : ""}`}
                    >
                      <div className="flex items-center gap-[8px]">
                        <IErrIconSvg />{" "}
                        <p className="mt-0">
                          <span className="font-medium">
                            {" "}
                            {rejectedFiles.length ?? 0} files couldn't be
                            trained
                          </span>{" "}
                          due to formatting errors or accessibility issues.
                        </p>
                      </div>
                      <div className="flex items-center gap-[8px]">
                        {retryRejectedLinks ? (
                          isRetraining ? (
                            <div className="rotating-svg">
                              <TrainingProgressIcon />
                            </div>
                          ) : (
                            <button
                              className="flex items-center gap-[8px]"
                              onClick={() => {
                                setSelectedStatus?.({
                                  text: "",
                                  icon: null,
                                });
                                retryRejectedLinks(rejectedFiles, "documents");
                              }}
                            >
                              {" "}
                              <RetrainBtnIcon /> Retrain
                            </button>
                          )
                        ) : null}
                        {/* <button>
                          {" "}
                          <DelIcon />{" "}
                        </button> */}
                      </div>
                    </div>
                  )
                )}

                <table
                  className={`links_table new-scrollbar w-100 mt-0 flex flex-col overflow-y-auto ${fromOnboarding ? "links_table-onboarding !h-full" : ""} ${inProcess && "pointer-events-none opacity-50"}`}
                >
                  <thead className="sticky top-0 z-[1] flex w-full bg-white">
                    <tr>
                      <th className="flex items-center">
                        {displayedTrainedFiles?.length > 0 &&
                          (fromOnboarding ? (
                            <input
                              className="onboarding-custom-checkbox"
                              type="checkbox"
                              checked={selectedAll}
                              onChange={selectAll}
                              disabled={isPostTrainingDelay}
                            />
                          ) : (
                            <label className="checkbox-container link-mark mb-0 flex w-[16px] items-center">
                              <input
                                className="bot-check h-[unset]"
                                type="checkbox"
                                checked={selectedAll}
                                onChange={selectAll}
                                disabled={isPostTrainingDelay}
                              />
                              <span className="mark">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <rect
                                    width="16"
                                    height="16"
                                    rx="5"
                                    fill="#FB5F66"
                                  />
                                  <path
                                    d="M11.6923 4.11088C11.3794 3.90411 10.9616 3.99489 10.7589 4.31353L6.9773 10.2532L5.13304 8.51563C4.85926 8.25774 4.43251 8.2747 4.17942 8.55368C3.92634 8.83243 3.94298 9.26775 4.21677 9.52541L6.63828 11.8068C6.63828 11.8068 6.70779 11.8677 6.73996 11.8891C6.85334 11.9642 6.98067 12 7.10665 12C7.32757 12 7.54421 11.8895 7.67356 11.6864L11.8912 5.06198C12.0941 4.74334 12.005 4.31742 11.6923 4.11088Z"
                                    fill="white"
                                  />
                                </svg>
                              </span>
                            </label>
                          ))}
                        <span
                          className={`${fromOnboarding ? " " : "pl-[16px]"} font-medium`}
                        >
                          File Name
                        </span>
                      </th>
                      <th>Status</th>
                      {!fromOnboarding && (
                        <th className="flex items-center gap-10">File Type</th>
                      )}
                      <th className="flex items-center gap-10 whitespace-nowrap">
                        Size
                      </th>
                      <th className="flex items-center gap-10 whitespace-nowrap">
                        Characters
                      </th>
                      {!fromOnboarding && (
                        <th className="flex items-center gap-10">
                          Last Updated
                        </th>
                      )}

                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody
                    className={`grey-scrollbar flex !h-max flex-col items-center justify-start ${rejectedFiles && rejectedFiles.length > 0 ? "err_dynamic_height" : "link-dynamic-height"} ${fromOnboarding ? "!h-full" : ""} `}
                  >
                    {displayedTrainedFiles.length > 0 ? (
                      displayedTrainedFiles?.map((file, index) => {
                        const actualIndex = fromOnboarding
                          ? index
                          : (fileCurrentPage - 1) * itemsPerPage + index;
                        return (
                          <tr
                            className={`w-100 flex flex-col ${inProcess ? "pointer-events-none opacity-50" : ""}`}
                            key={getFileSelectionKey(file) || actualIndex}
                          >
                            <td className="flex items-center">
                              {fromOnboarding ? (
                                <input
                                  className="onboarding-custom-checkbox"
                                  type="checkbox"
                                  checked={selectedIds.some(
                                    (item: any) =>
                                      getFileSelectionKey(item) ===
                                      getFileSelectionKey(file),
                                  )}
                                  onChange={() => toggleSelection(file)}
                                  disabled={isPostTrainingDelay}
                                />
                              ) : (
                                <label className="checkbox-container link-mark mb-0 flex w-[16px] items-center">
                                  <input
                                    className="bot-check h-[unset]"
                                    type="checkbox"
                                    checked={selectedIds.some(
                                      (item: any) =>
                                        getFileSelectionKey(item) ===
                                        getFileSelectionKey(file),
                                    )}
                                    onChange={() => toggleSelection(file)}
                                    disabled={isPostTrainingDelay}
                                  />
                                  <span className="mark !w-[18px]">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 16 16"
                                      fill="none"
                                    >
                                      <rect
                                        width="16"
                                        height="16"
                                        rx="5"
                                        fill="#FB5F66"
                                      />
                                      <path
                                        d="M11.6923 4.11088C11.3794 3.90411 10.9616 3.99489 10.7589 4.31353L6.9773 10.2532L5.13304 8.51563C4.85926 8.25774 4.43251 8.2747 4.17942 8.55368C3.92634 8.83243 3.94298 9.26775 4.21677 9.52541L6.63828 11.8068C6.63828 11.8068 6.70779 11.8677 6.73996 11.8891C6.85334 11.9642 6.98067 12 7.10665 12C7.32757 12 7.54421 11.8895 7.67356 11.6864L11.8912 5.06198C12.0941 4.74334 12.005 4.31742 11.6923 4.11088Z"
                                        fill="white"
                                      />
                                    </svg>
                                  </span>
                                </label>
                              )}
                              <span
                                className="fx trainedLinksUrl ml-[16px] gap-8 overflow-hidden whitespace-nowrap"
                                onMouseEnter={(e) => {
                                  const container = e.currentTarget;
                                  const inner = container.querySelector(
                                    ".scrolling-text",
                                  ) as HTMLElement | null;
                                  if (inner) {
                                    const scrollDistance =
                                      inner.scrollWidth - container.offsetWidth;
                                    if (scrollDistance > 0) {
                                      // Set a fixed speed of 100 pixels per second
                                      const speed = 100; // pixels per second
                                      const duration = scrollDistance / speed;
                                      inner.style.transition = `transform ${duration}s linear`;
                                      inner.style.transform = `translateX(-${scrollDistance}px)`;
                                    }
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const container = e.currentTarget;
                                  const inner = container.querySelector(
                                    ".scrolling-text",
                                  ) as HTMLElement | null;
                                  if (inner) {
                                    inner.style.transition =
                                      "transform 0.3s ease-out";
                                    inner.style.transform = "translateX(0)";
                                  }
                                }}
                              >
                                <div
                                  className={`scrolling-text flex items-center ${fromOnboarding ? "gap-[5px]" : "gap-2"} whitespace-nowrap`}
                                >
                                  {fromOnboarding &&
                                    getFileIcon(file?.title || file?.name)}
                                  <span> {file?.title || file?.name}</span>
                                </div>
                              </span>
                            </td>
                            <td
                              className={`flex h-full items-center gap-[7px] whitespace-nowrap ${
                                file?.status === "In Progress"
                                  ? "rotating-svg"
                                  : ""
                              } ${
                                file?.status === "Re-syncing"
                                  ? "resyncing-svg"
                                  : ""
                              }`}
                            >
                              {fromOnboarding
                                ? onboardingStatusIcons[file.status]
                                : statuses.find(
                                    (item) => item.text === file.status,
                                  )?.iconComponent}
                              {file.status}
                            </td>

                            {!fromOnboarding && (
                              <td>
                                {renderFileType(file?.title || file?.name)}
                              </td>
                            )}
                            <td>
                              {file?.size / (1024 * 1024) >= 0.01
                                ? (file?.size / (1024 * 1024)).toFixed(2) +
                                  " MB"
                                : (file?.size / 1024).toFixed(2) + " KB"}
                            </td>

                            <td
                              className={`flex h-full items-center gap-[7px] whitespace-nowrap ${
                                file?.status === "In Progress"
                                  ? "rotating-svg"
                                  : ""
                              } ${
                                file?.status === "Re-syncing"
                                  ? "resyncing-svg"
                                  : ""
                              }`}
                            >
                              {fromOnboarding
                                ? onboardingStatusIcons[file.status]
                                : statuses.find(
                                    (item) => item.text === file.status,
                                  )?.iconComponent}
                              {file.status == "Training Complete"
                                ? useFormatNumber(file?.characterCount)
                                : file.status == "File Rejected"
                                  ? "Failed"
                                  : "Syncing"}
                            </td>
                            {!fromOnboarding && (
                              <td>
                                {isNaN(Number(file.lastSyncTime)) ||
                                !file.lastSyncTime
                                  ? "..."
                                  : moment(Number(file.lastSyncTime)).fromNow()}
                              </td>
                            )}
                            <td
                              className="files_action_drp relative flex items-center justify-center"
                              onMouseLeave={() =>
                                !fromOnboarding &&
                                handleFileActionDropdowns(
                                  index,
                                  "fileAction",
                                  false,
                                )
                              }
                            >
                              {fromOnboarding ? (
                                <button
                                  type="button"
                                  title="Delete file"
                                  aria-label="Delete file"
                                  onClick={() => {
                                    handleFileRemove(deleteTrainDocument, {
                                      source: file,
                                      size: file.size,
                                      sourceType: "documents",
                                      fileId: file.id,
                                    });
                                  }}
                                >
                                  <WithoutBgDeleteIcon />
                                </button>
                              ) : (
                                <>
                                  <svg
                                    className="cursor-pointer"
                                    ref={(el) => {
                                      if (!dropdownRef.current)
                                        dropdownRef.current = {};
                                      dropdownRef.current[
                                        `fileAction-${index}`
                                      ] = el;
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFileActionDropdowns(
                                        index,
                                        "fileAction",
                                        !dropdowns[index]?.fileAction,
                                      );
                                    }}
                                    width={17}
                                    height={16}
                                    viewBox="0 0 17 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <g clipPath="url(#clip0_190_17855)">
                                      <path
                                        d="M8.49966 3.33331C9.42013 3.33331 10.1663 2.58713 10.1663 1.66666C10.1663 0.746187 9.42013 0 8.49966 0C7.5792 0 6.83301 0.746187 6.83301 1.66666C6.83301 2.58713 7.5792 3.33331 8.49966 3.33331Z"
                                        fill="#444444"
                                      />
                                      <path
                                        d="M8.49966 9.66632C9.42013 9.66632 10.1663 8.92013 10.1663 7.99966C10.1663 7.0792 9.42013 6.33301 8.49966 6.33301C7.5792 6.33301 6.83301 7.0792 6.83301 7.99966C6.83301 8.92013 7.5792 9.66632 8.49966 9.66632Z"
                                        fill="#444444"
                                      />
                                      <path
                                        d="M8.49966 16.0003C9.42013 16.0003 10.1663 15.2541 10.1663 14.3336C10.1663 13.4142 9.32541 12.668 8.40494 12.668C7.48447 12.668 6.73828 13.4142 6.73828 14.3336C6.73828 15.2541 7.48447 16.0003 8.40494 16.0003Z"
                                        fill="#444444"
                                      />
                                    </g>
                                    <defs>
                                      <clipPath id="clip0_190_17855">
                                        <rect
                                          width={16}
                                          height={16}
                                          fill="white"
                                          transform="translate(0.5)"
                                        />
                                      </clipPath>
                                    </defs>
                                  </svg>
                                  {dropdowns[index]?.fileAction &&
                                    createPortal(
                                      <ul
                                        id={`dropdown-fileAction-${index}`}
                                        className={`status_dropdown_list ${fromOutgrow ? "outgrow-dropdown" : ""} absolute z-10 !w-[150px] overflow-hidden rounded-[8px]`}
                                        style={{
                                          top: `${dropdownPosition.top}px`,
                                          left: `${dropdownPosition.left}px`,
                                          zIndex: 999,
                                          ...(fromOutgrow && {
                                            fontFamily: '"Ubuntu", sans-serif',
                                          }),
                                        }}
                                      >
                                        {handleViewDetails && (
                                          <li
                                            className={`flex cursor-pointer items-center gap-[6px] ${file?.status === "In Progress" ? "pointer-events-none opacity-50" : ""}`}
                                            onClick={() => {
                                              handleViewDetails(
                                                file.title,
                                                file.status,
                                                "",
                                                index,
                                                "file",
                                              );
                                              handleFileActionDropdowns(
                                                index,
                                                "fileAction",
                                                false,
                                              );
                                            }}
                                          >
                                            <ViewDetailsIcon />
                                            View Details
                                          </li>
                                        )}
                                        <li
                                          className="flex cursor-pointer items-center gap-[6px]"
                                          onClick={() => {
                                            handleFileRemove(
                                              deleteTrainDocument,

                                              {
                                                source: file,
                                                size: file.size,
                                                sourceType: "documents",
                                                fileId: file.id,
                                              },
                                            );
                                            handleFileActionDropdowns(
                                              index,
                                              "fileAction",
                                              false,
                                            );
                                          }}
                                        >
                                          <DelIconSvg />
                                          Delete
                                        </li>
                                      </ul>,
                                      document.getElementById(
                                        "dropdown-portal",
                                      ) as HTMLElement,
                                    )}
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <div className="no_links_fetched pd-30 h-100 grid place-items-center">
                        <img
                          src={
                            fromOutgrow
                              ? "https://dlvkyia8i4zmz.cloudfront.net/IBeTtKfoTCugBIFzF3vR_No_links___illustration__1_.svg"
                              : "https://dlvkyia8i4zmz.cloudfront.net/7eUKmJsvR0OTNIC4DKVQ_1.svg"
                          }
                          alt="no links fetched"
                        />
                      </div>
                    )}
                  </tbody>
                </table>
                {!fromOnboarding && (
                  <div
                    className={`border-t-[rgba(0, 0, 0, 0.10)] flex h-[60px] w-full items-center justify-between overflow-hidden rounded-b-[10px] border-t bg-[#f9f9f9] px-[30px] py-[16px] ${fromOnboarding ? "trained_links_footer-onboarding" : ""}`}
                  >
                    <Pagination
                      currentPage={fileCurrentPage}
                      totalPages={totalFilteredPages}
                      totalItems={filteredTrainedFiles.length}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handleFilePageChange}
                      showFirstLast={true}
                      showItemsInfo={true}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="no_links_fetched pd-30 h-100 grid place-items-center">
                <img
                  src={
                    fromOutgrow
                      ? "https://dlvkyia8i4zmz.cloudfront.net/IBeTtKfoTCugBIFzF3vR_No_links___illustration__1_.svg"
                      : "https://dlvkyia8i4zmz.cloudfront.net/7eUKmJsvR0OTNIC4DKVQ_1.svg"
                  }
                  alt="no links fetched"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default UploadFiles;
